import { useRef, useState, useCallback } from 'react';

const NUM_FREQ_BARS = 32;

export function useLiveRecorder(onComplete: (audioB64: string) => void) {
  const [isRecording, setIsRecording] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [inputLevel, setInputLevel] = useState(0);
  const [freqBars, setFreqBars] = useState<number[]>(() => new Array(NUM_FREQ_BARS).fill(0));
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const animFrameRef = useRef<number>(0);

  const updateLevel = useCallback(() => {
    if (!analyserRef.current) return;

    // Time-domain for RMS level
    const timeData = new Uint8Array(analyserRef.current.fftSize);
    analyserRef.current.getByteTimeDomainData(timeData);
    let sum = 0;
    for (let i = 0; i < timeData.length; i++) {
      const v = (timeData[i] - 128) / 128;
      sum += v * v;
    }
    setInputLevel(Math.sqrt(sum / timeData.length));

    // Frequency data for visualizer bars
    const freqData = new Uint8Array(analyserRef.current.frequencyBinCount);
    analyserRef.current.getByteFrequencyData(freqData);
    const step = Math.floor(freqData.length / NUM_FREQ_BARS);
    const bars: number[] = [];
    for (let i = 0; i < NUM_FREQ_BARS; i++) {
      let max = 0;
      for (let j = 0; j < step; j++) {
        const val = freqData[i * step + j];
        if (val > max) max = val;
      }
      bars.push(max / 255);
    }
    setFreqBars(bars);

    if (mediaRecorderRef.current?.state === 'recording') {
      animFrameRef.current = requestAnimationFrame(updateLevel);
    }
  }, []);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100,
        },
      });
      streamRef.current = stream;

      // Set up analyser for input level
      const ctx = new AudioContext();
      audioContextRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      analyserRef.current = analyser;

      // Determine codec
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm';

      const recorder = new MediaRecorder(stream, { mimeType });
      const chunks: BlobPart[] = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };

      recorder.onstop = () => {
        setIsSubmitting(true);
        const blob = new Blob(chunks, { type: mimeType });
        const reader = new FileReader();
        reader.onloadend = () => {
          const result = reader.result as string;
          const b64 = result.split(',')[1] || '';
          onComplete(b64);
          // submitting stays true until the parent resets phase (backend responds)
        };
        reader.readAsDataURL(blob);
      };

      mediaRecorderRef.current = recorder;
      recorder.start();
      setIsRecording(true);
      animFrameRef.current = requestAnimationFrame(updateLevel);
    } catch (err) {
      console.error('Failed to start recording:', err);
    }
  }, [onComplete, updateLevel]);

  const stopRecording = useCallback(() => {
    cancelAnimationFrame(animFrameRef.current);
    setInputLevel(0);
    setFreqBars(new Array(NUM_FREQ_BARS).fill(0));

    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    audioContextRef.current?.close();

    setIsRecording(false);
    mediaRecorderRef.current = null;
    streamRef.current = null;
    analyserRef.current = null;
    audioContextRef.current = null;
  }, []);

  const resetSubmitting = useCallback(() => {
    setIsSubmitting(false);
  }, []);

  return { isRecording, isSubmitting, inputLevel, freqBars, startRecording, stopRecording, resetSubmitting };
}
